// ORI-677 — approved-vendor welcome-banner storage helpers, shared across the banner host
// (useListingLayout) and EVERY auth-teardown path (AuthContext.signOut / signOutAndClearOnly
// and the global 401 handler in helper/api). Kept in one module so the key prefix and the
// latch-clear discipline cannot drift between them — the same "reset paths must stay in sync
// with auth teardown" contract resetUserProfileBootGuard already models.
//
// Two markers reconcile guarantees that pull in opposite directions (decision logic lives in
// useListingLayout):
//   • DURABLE — localStorage + the server `welcome_banner_seen` flag → suppresses the banner
//     across logins (AC-1).
//   • SESSION latch — sessionStorage, plus an in-memory mirror for blocked storage → keeps the
//     banner visible across in-visit navigation until the X clears it (AC-6), and is cleared at
//     every logout so "shown this visit" never leaks into the next login.

export const APPROVED_BANNER_SEEN_PREFIX = 'vendor-approved-banner-seen';
const SESSION_LATCH_PREFIX = `${APPROVED_BANNER_SEEN_PREFIX}:session:`;
// ORI-660 (the currently-shipped code) persisted a dismissal under this prefix. ORI-677
// renamed the durable marker to `...seen:{id}`, so a vendor who already dismissed under the
// old key would otherwise see the banner one more time after this deploys (their new-key
// marker is absent and BE #1766's server flag isn't set yet). We READ the legacy key for one
// release to honor those dismissals; nothing writes it anymore, so it ages out naturally.
const LEGACY_DISMISSED_PREFIX = 'vendor-approved-banner-dismissed';
const DURABLE_STORES = ['localStorage', 'sessionStorage'] as const;

export const localSeenKey = (onboardingId: string): string =>
  `${APPROVED_BANNER_SEEN_PREFIX}:${onboardingId}`;

export const legacyDismissedKey = (onboardingId: string): string =>
  `${LEGACY_DISMISSED_PREFIX}:${onboardingId}`;

export const sessionShownKey = (onboardingId: string): string =>
  `${SESSION_LATCH_PREFIX}${onboardingId}`;

// In-memory mirror of the session latch. sessionStorage.setItem can throw or silently no-op
// (Safari "block all cookies", sandboxed iframes, quota-full). Without this fallback a blocked
// write would drop the latch, and the banner would vanish on the first navigation — verbatim
// AC-6's regression. Module scope, so it lives for the tab like sessionStorage does and is
// wiped by clearAllVendorBannerSessionLatches at logout.
const inMemoryLatches = new Set<string>();

export const wasApprovedBannerSeen = (storageKey: string): boolean =>
  DURABLE_STORES.some((store) => {
    try {
      return window[store].getItem(storageKey) === 'true';
    } catch (_error) {
      return false;
    }
  });

export const markApprovedBannerSeen = (storageKey: string): void => {
  DURABLE_STORES.forEach((store) => {
    try {
      window[store].setItem(storageKey, 'true');
    } catch (_error) {
      // Best-effort persistence; the live UI still updates if storage is blocked.
    }
  });
};

export const wasShownThisSession = (sessionKey: string): boolean => {
  if (inMemoryLatches.has(sessionKey)) return true;
  try {
    return sessionStorage.getItem(sessionKey) === 'true';
  } catch (_error) {
    return false;
  }
};

export const setShownThisSession = (sessionKey: string): void => {
  // Record in memory FIRST, so a blocked sessionStorage can't drop the latch (AC-6).
  inMemoryLatches.add(sessionKey);
  try {
    sessionStorage.setItem(sessionKey, 'true');
  } catch (_error) {
    // Best-effort; the in-memory mirror keeps AC-6 intact until the tab closes.
  }
};

export const clearShownThisSession = (sessionKey: string): void => {
  inMemoryLatches.delete(sessionKey);
  try {
    sessionStorage.removeItem(sessionKey);
  } catch (_error) {
    // no-op
  }
};

/**
 * Clear EVERY welcome-banner session latch — the in-memory mirror and every prefix-matched
 * sessionStorage key. Called from ALL auth-teardown paths (AuthContext.signOut,
 * signOutAndClearOnly, and helper/api's global 401 handler) so "shown this visit" never
 * outlives the session and re-shows a banner that should be suppressed on the next login
 * (AC-1). Prefix-scoped, so the DURABLE `...seen:{onboardingId}` marker (no `:session:`) — the
 * dismissal record that MUST survive logout — is left untouched.
 */
export const clearAllVendorBannerSessionLatches = (): void => {
  inMemoryLatches.clear();
  try {
    // Reverse iteration: removeItem re-indexes sessionStorage, so a forward loop would skip
    // the entry after each removal.
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(SESSION_LATCH_PREFIX)) sessionStorage.removeItem(key);
    }
  } catch (_error) {
    // Best-effort; a blocked sessionStorage means there is no persisted latch to clear.
  }
};
